// src/features/dashboard/pages/EmployeeLoggedHoursReport.tsx

import {
  useEffect,
  useRef,
} from 'react';

import {
  useLoaderContext,
} from '../../../../components/Loader/LoaderContext';

import DashboardHeader from '../../components/DashboardHeader';

import LoggedHoursHeader from '../../components/EmployeeLoggedHoursReport/LoggedHoursHeader';

import LoggedHoursKpiCards from '../../components/EmployeeLoggedHoursReport/LoggedHoursKpiCards';

import HourlyActivityLog from '../../components/EmployeeLoggedHoursReport/HourlyActivityLog';

import SessionLog from '../../components/EmployeeLoggedHoursReport/SessionLog';

import WeeklyHoursChart from '../../components/EmployeeLoggedHoursReport/WeeklyHoursChart';

import MonthlyHoursTrend from '../../components/EmployeeLoggedHoursReport/MonthlyHoursTrend';

import TodayTimeSplit from '../../components/EmployeeLoggedHoursReport/TodayTimeSplit';

import MyHoursVsTeamAverage from '../../components/EmployeeLoggedHoursReport/MyHoursVsTeamAverage';

import DailyHoursLogTable from '../../components/EmployeeLoggedHoursReport/DailyHoursLogTable';

import {
  useEmployeeLoggedHours,
} from '../../hooks/Employee/useEmployeeLoggedHours';


/* ============================================================================
 * BROWSER ZOOM AUTO REFRESH
 *
 * This detects Chrome browser zoom changes such as:
 *
 *      80%
 *       ↓
 *     100%
 *       ↓
 *     125%
 *
 * Chrome changes window.devicePixelRatio when browser zoom changes.
 *
 * We reload ONLY when the devicePixelRatio changes.
 *
 * Normal browser resizing does NOT reload the page.
 * ========================================================================== */

const useBrowserZoomRefresh = () => {

  const initialDevicePixelRatio =
    useRef<number>(
      window.devicePixelRatio,
    );

  const reloadStarted =
    useRef<boolean>(
      false,
    );

  useEffect(() => {

    let resizeTimer:
      number | null = null;


    /* ========================================================================
     * CHECK ZOOM
     * ====================================================================== */

    const checkZoom =
      () => {

        const currentDevicePixelRatio =
          window.devicePixelRatio;

        const previousDevicePixelRatio =
          initialDevicePixelRatio.current;


        /* ====================================================================
         * ZOOM CHANGED
         * ================================================================== */

        if (
          currentDevicePixelRatio !==
          previousDevicePixelRatio
        ) {

          /*
           * Chrome can fire several resize events while the zoom value is
           * changing. Prevent multiple reload calls.
           */

          if (
            reloadStarted.current
          ) {

            return;
          }


          reloadStarted.current =
            true;


          console.log(
            '====================================================',
          );

          console.log(
            'BROWSER ZOOM CHANGE DETECTED',
          );

          console.log(
            'Previous devicePixelRatio:',
            previousDevicePixelRatio,
          );

          console.log(
            'Current devicePixelRatio:',
            currentDevicePixelRatio,
          );

          console.log(
            'Automatically refreshing page...',
          );

          console.log(
            '====================================================',
          );


          /*
           * Give Chrome a small amount of time to finish applying the new
           * zoom level before refreshing.
           */

          window.setTimeout(
            () => {

              window.location.reload();

            },
            150,
          );

        }
      };


    /* ========================================================================
     * WINDOW RESIZE
     * ====================================================================== */

    const handleResize =
      () => {

        if (
          resizeTimer !== null
        ) {

          window.clearTimeout(
            resizeTimer,
          );
        }


        resizeTimer =
          window.setTimeout(
            () => {

              checkZoom();

            },
            100,
          );

      };


    /* ========================================================================
     * VISUAL VIEWPORT RESIZE
     *
     * Chrome browser zoom can also trigger visualViewport resize.
     * ====================================================================== */

    const visualViewport =
      window.visualViewport;


    /* ========================================================================
     * WINDOW LISTENER
     * ====================================================================== */

    window.addEventListener(
      'resize',
      handleResize,
      {
        passive: true,
      },
    );


    /* ========================================================================
     * VISUAL VIEWPORT LISTENER
     * ====================================================================== */

    if (
      visualViewport
    ) {

      visualViewport.addEventListener(
        'resize',
        handleResize,
      );
    }


    /* ========================================================================
     * MEDIA QUERY ZOOM DETECTION
     *
     * This gives us an additional way to detect Chrome zoom changes.
     *
     * Example:
     *
     * 80%  -> resolution changes
     * 100% -> resolution changes
     * 125% -> resolution changes
     * ====================================================================== */

    let mediaQuery:
      MediaQueryList | null = null;


    const attachResolutionListener =
      () => {

        /*
         * Remove the previous resolution listener before creating a new one.
         */

        if (
          mediaQuery
        ) {

          mediaQuery.removeEventListener(
            'change',
            handleResolutionChange,
          );
        }


        /*
         * Create a media query using the CURRENT DPR.
         *
         * When Chrome zoom changes, this resolution query becomes invalid
         * and fires a change event.
         */

        mediaQuery =
          window.matchMedia(
            `(resolution: ${window.devicePixelRatio}dppx)`,
          );


        mediaQuery.addEventListener(
          'change',
          handleResolutionChange,
        );
      };


    function handleResolutionChange() {

      checkZoom();

      /*
       * If the page has not reloaded for some reason, attach the listener
       * again using the latest DPR.
       */

      if (
        !reloadStarted.current
      ) {

        attachResolutionListener();
      }
    }


    /*
     * Attach initial resolution listener.
     */

    attachResolutionListener();


    /* ========================================================================
     * CLEANUP
     * ====================================================================== */

    return () => {

      window.removeEventListener(
        'resize',
        handleResize,
      );


      if (
        visualViewport
      ) {

        visualViewport.removeEventListener(
          'resize',
          handleResize,
        );
      }


      if (
        mediaQuery
      ) {

        mediaQuery.removeEventListener(
          'change',
          handleResolutionChange,
        );
      }


      if (
        resizeTimer !== null
      ) {

        window.clearTimeout(
          resizeTimer,
        );
      }

    };

  }, []);
};


/* ============================================================================
 * LOADER
 * ========================================================================== */

const EmployeeLoggedHoursLoader = () => {

  return (
    <div
      className="
        flex
        min-h-[560px]
        w-full
        flex-col
        items-center
        justify-center
        rounded-[12px]
        bg-[#F8F9FB]
      "
    >

      {/* ================================================================
          SPINNER
      ================================================================ */}

      <div
        className="
          h-[40px]
          w-[40px]
          animate-spin
          rounded-full
          border-[4px]
          border-[#E4E7EC]
          border-t-[#2563EB]
        "
      />


      {/* ================================================================
          LOADING TEXT
      ================================================================ */}

      <p
        className="
          mt-[12px]
          text-[13px]
          font-medium
          text-[#475467]
        "
      >
        Loading logged hours...
      </p>


      <p
        className="
          mt-[4px]
          text-[11px]
          text-[#98A2B3]
        "
      >
        Fetching your latest activity
      </p>

    </div>
  );
};


/* ============================================================================
 * PAGE
 * ========================================================================== */

const EmployeeLoggedHoursReport = () => {

  const {
    hideLoader,
  } = useLoaderContext();


  /* ==========================================================================
     BROWSER ZOOM AUTO REFRESH

     IMPORTANT:
     This must be inside the page component so it runs while this page is
     mounted.

     It automatically refreshes when Chrome zoom changes.
     ========================================================================== */

  useBrowserZoomRefresh();


  /* ==========================================================================
     EMPLOYEE LOGGED HOURS
     ========================================================================== */

  const {
    loading,
    kpi,
    sessionLog,
    wtdLogged,
    trend30Day,
    timeSplit,
    meVsTeam,
    dailyLog,
    hourlyLogged,
  } = useEmployeeLoggedHours();


  /* ==========================================================================
     GLOBAL LOADER

     Keep the existing global loader behavior.

     Once API loading is complete, hide the global loader after
     a small delay so there is no abrupt transition.
     ========================================================================== */

  useEffect(() => {

    if (
      !loading
    ) {

      const timer =
        window.setTimeout(
          () => {

            hideLoader();

          },
          500,
        );


      return () => {

        window.clearTimeout(
          timer,
        );

      };

    }


    return undefined;

  }, [
    loading,
    hideLoader,
  ]);


  /* ==========================================================================
     PAGE
     ========================================================================== */

  return (

    <div
      className="
        min-h-screen
        bg-[#F4F5F7]
        px-[6px]
        pb-[8px]

        min-w-0
        w-full
        max-w-full

        overflow-x-hidden
      "
    >

      {/* ======================================================================
          DASHBOARD HEADER
      ====================================================================== */}

      <DashboardHeader
        showBackButton={true}
        breadcrumbs={[
          {
            label:
              'Work Vista',

            path:
              '/',
          },

          {
            label:
              'My Dashboard',

            path:
              '/employee-dashboard',
          },

          {
            label:
              'Logged Hours',
          },
        ]}
      />


      {/* ======================================================================
          MAIN CONTENT
      ====================================================================== */}

      <div
        className="
          -mt-[14px]

          w-full
          min-w-0
          max-w-full

          overflow-hidden

          rounded-[16px]

          border
          border-[#D0D5DD]

          bg-[#F8F9FB]

          p-[16px]
        "
      >

        {/* ====================================================================
            API LOADING
        ==================================================================== */}

        {loading ? (

          <EmployeeLoggedHoursLoader />

        ) : (

          <>

            {/* ================================================================
                LOGGED HOURS HEADER
            ================================================================ */}

            <LoggedHoursHeader
              kpi={
                kpi
              }
            />


            {/* ================================================================
                KPI CARDS
            ================================================================ */}

            <div
              className="
                mt-4
                min-w-0
                w-full
                max-w-full
              "
            >

              <LoggedHoursKpiCards
                kpi={
                  kpi
                }
              />

            </div>


            {/* ================================================================
                HOURLY ACTIVITY + SESSION LOG

                IMPORTANT:
                Use lg instead of xl so the two sections remain side-by-side
                at normal desktop resolutions and 100%-125% browser zoom.
            ================================================================ */}

            <div
              className="
                mt-4

                grid
                w-full
                min-w-0
                max-w-full

                grid-cols-1
                gap-4

                lg:grid-cols-12
                lg:items-stretch
              "
            >

              {/* ==============================================================
                  TODAY'S HOUR-BY-HOUR LOG
              ============================================================== */}

              <div
                className="
                  min-w-0
                  w-full
                  max-w-full

                  overflow-hidden

                  lg:col-span-8
                "
              >

                <HourlyActivityLog
                  data={
                    hourlyLogged
                  }

                  kpi={
                    kpi
                  }
                />

              </div>


              {/* ==============================================================
                  SESSION LOG
              ============================================================== */}

              <div
                className="
                  min-w-0
                  w-full
                  max-w-full

                  overflow-hidden

                  lg:col-span-4
                "
              >

                <SessionLog
                  sessions={
                    sessionLog
                  }

                  data={
                    hourlyLogged
                  }

                  kpi={
                    kpi
                  }
                />

              </div>

            </div>


            {/* ================================================================
                WEEKLY HOURS + 30-DAY HOURS TREND

                IMPORTANT:
                lg:grid-cols-12 makes both charts stay side-by-side earlier
                than the previous xl breakpoint.

                Weekly = 7 columns
                Monthly = 5 columns
            ================================================================ */}

            <div
              className="
                mt-4

                grid
                w-full
                min-w-0
                max-w-full

                grid-cols-1
                gap-4

                lg:grid-cols-12
                lg:items-stretch
              "
            >

              {/* ==============================================================
                  WEEKLY HOURS
              ============================================================== */}

              <div
                className="
                  min-w-0
                  w-full
                  max-w-full

                  overflow-hidden

                  lg:col-span-7
                "
              >

                <WeeklyHoursChart
                  data={
                    wtdLogged
                  }
                />

              </div>


              {/* ==============================================================
                  30-DAY HOURS TREND
              ============================================================== */}

              <div
                className="
                  min-w-0
                  w-full
                  max-w-full

                  overflow-hidden

                  lg:col-span-5
                "
              >

                <MonthlyHoursTrend
                  data={
                    trend30Day
                  }
                />

              </div>

            </div>


            {/* ================================================================
                TODAY TIME SPLIT + ME VS TEAM

                Today Time Split = 4 columns
                My Hours Vs Team = 8 columns

                Both remain side-by-side at lg and above.
            ================================================================ */}

            <div
              className="
                mt-4

                grid
                w-full
                min-w-0
                max-w-full

                grid-cols-1
                gap-4

                lg:grid-cols-12
                lg:items-stretch
              "
            >

              {/* ==============================================================
                  TODAY TIME SPLIT
              ============================================================== */}

              <div
                className="
                  min-w-0
                  w-full
                  max-w-full

                  overflow-hidden

                  lg:col-span-4
                "
              >

                <TodayTimeSplit
                  data={
                    timeSplit
                  }

                  kpi={
                    kpi
                  }
                />

              </div>


              {/* ==============================================================
                  MY HOURS VS TEAM AVERAGE
              ============================================================== */}

              <div
                className="
                  min-w-0
                  w-full
                  max-w-full

                  overflow-hidden

                  lg:col-span-8
                "
              >

                <MyHoursVsTeamAverage
                  data={
                    meVsTeam
                  }
                />

              </div>

            </div>


            {/* ================================================================
                DAILY HOURS LOG
            ================================================================ */}

            <div
              className="
                mt-4

                min-w-0
                w-full
                max-w-full

                overflow-hidden
              "
            >

              <DailyHoursLogTable
                data={
                  dailyLog
                }
              />

            </div>

          </>

        )}

      </div>

    </div>
  );
};


export default EmployeeLoggedHoursReport;